import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpBackend,
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

// ── Shared refresh state (module-level — one concurrent refresh at a time) ───
let isRefreshing = false;
const refreshToken$ = new BehaviorSubject<string | null>(null);

// Angular context token — in-memory only, never sent as an HTTP header
const RETRIED = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router  = inject(Router);
  const backend = inject(HttpBackend);   // bypass interceptor for refresh call

  const token = localStorage.getItem('access_token');
  if (token) {
    req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Skip refresh only for actual auth endpoints (login, register, refresh, OTP)
      // Note: /api/auth/admin/* are protected API routes, NOT auth calls — they need refresh
      const isAuthCall =
        req.url.includes('/api/auth/login') ||
        req.url.includes('/api/auth/register') ||
        req.url.includes('/api/auth/refresh') ||
        req.url.includes('/api/auth/forgot') ||
        req.url.includes('/api/auth/reset') ||
        req.url.includes('/api/auth/verify') ||
        req.url.includes('/company/auth/');
      // Don't retry a request that was already retried after a refresh — prevents infinite loop
      const alreadyRetried = req.context.get(RETRIED);
      // Only 401 means "your token is invalid/expired, try refreshing it". A 403 means
      // the token is fine but the action is forbidden (e.g. a coordinator missing a
      // permission) — that must surface as an error, not force a full logout.
      if (err.status === 401 && !isAuthCall && !alreadyRetried) {
        return handleRefresh(req, next, router, backend);
      }
      // A retried request that STILL comes back 401 means the refresh succeeded (a fresh token
      // was issued) but the request was rejected anyway — e.g. the account was suspended after
      // the token was minted (company-service's isCompanyActive check). Previously this just
      // propagated the error silently, leaving the user in a logged-in-looking UI that 401s on
      // every subsequent action instead of being cleanly signed out.
      if (err.status === 401 && alreadyRetried) {
        doLogout(router);
      }
      return throwError(() => err);
    })
  );
};

function handleRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  router: Router,
  backend: HttpBackend
): Observable<HttpEvent<unknown>> {
  // If a refresh is already in-flight, queue this request until it resolves
  if (isRefreshing) {
    return refreshToken$.pipe(
      filter(t => t !== null),
      take(1),
      switchMap(newToken =>
        next(req.clone({
          setHeaders: { Authorization: `Bearer ${newToken}` },
          context: new HttpContext().set(RETRIED, true),
        }))
      )
    );
  }

  isRefreshing = true;
  refreshToken$.next(null);

  const storedRefresh = localStorage.getItem('refresh_token');
  if (!storedRefresh) {
    isRefreshing = false;
    doLogout(router);
    return throwError(() => new Error('No refresh token'));
  }

  // Use HttpBackend directly to avoid going through this interceptor again
  const http = new HttpClient(backend);
  return http
    .post<any>(`${environment.apiUrl}/api/auth/refresh`, { refreshToken: storedRefresh })
    .pipe(
      switchMap((res: any) => {
        isRefreshing = false;
        const newAccess  = res?.data?.accessToken  ?? res?.accessToken;
        const newRefresh = res?.data?.refreshToken ?? res?.refreshToken;

        if (!newAccess) {
          doLogout(router);
          return throwError(() => new Error('Refresh response missing token'));
        }

        localStorage.setItem('access_token', newAccess);
        if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
        refreshToken$.next(newAccess);

        // Retry the original request — context token prevents a second refresh loop (never sent to server)
        return next(req.clone({
          setHeaders: { Authorization: `Bearer ${newAccess}` },
          context: new HttpContext().set(RETRIED, true),
        }));
      }),
      catchError(err => {
        isRefreshing = false;
        doLogout(router);
        return throwError(() => err);
      })
    );
}

function doLogout(router: Router): void {
  const role = localStorage.getItem('user_role');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') {
    router.navigate(['/rx-portal/b7f3e2a9']);
  } else if (role === 'COMPANY') {
    router.navigate(['/auth/company-login']);
  } else if (role === 'COORDINATOR') {
    router.navigate(['/auth/coordinator-login']);
  } else if (role === 'TRAINER') {
    router.navigate(['/auth/trainer-login']);
  } else {
    router.navigate(['/auth/login']);
  }
}
